"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConfigureTeamsAppDriver = exports.internalOutputNames = exports.actionName = void 0;
const tslib_1 = require("tslib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const fs_extra_1 = tslib_1.__importDefault(require("fs-extra"));
const lib_1 = require("@feathersjs/hooks/lib");
const isUUID_1 = tslib_1.__importDefault(require("validator/lib/isUUID"));
const lodash_1 = require("lodash");
const wrapUtil_1 = require("../util/wrapUtil");
const addStartAndEndTelemetry_1 = require("../middleware/addStartAndEndTelemetry");
const appStudioClient_1 = require("./clients/appStudioClient");
const results_1 = require("./results");
const telemetry_1 = require("./utils/telemetry");
const ManifestUtils_1 = require("./utils/ManifestUtils");
const errors_1 = require("./errors");
const tools_1 = require("../../../common/tools");
const localizeUtils_1 = require("../../../common/localizeUtils");
const typedi_1 = require("typedi");
const common_1 = require("../../utils/common");
const common_2 = require("../../../error/common");
exports.actionName = "teamsApp/update";
exports.internalOutputNames = {
    teamsAppUpdateTime: "TEAMS_APP_UPDATE_TIME",
    teamsAppTenantId: "TEAMS_APP_TENANT_ID",
};
let ConfigureTeamsAppDriver = class ConfigureTeamsAppDriver {
    constructor() {
        this.description = localizeUtils_1.getLocalizedString("driver.teamsApp.description.updateDriver");
        this.progressTitle = localizeUtils_1.getLocalizedString("driver.teamsApp.progressBar.updateTeamsAppStepMessage");
    }
    async execute(args, context, outputEnvVarNames) {
        const wrapContext = new wrapUtil_1.WrapDriverContext(context, exports.actionName, exports.actionName);
        const res = await this.update(args, wrapContext, outputEnvVarNames);
        return {
            result: res,
            summaries: wrapContext.summaries,
        };
    }
    async update(args, context, outputEnvVarNames) {
        telemetry_1.TelemetryUtils.init(context);
        const result = this.validateArgs(args);
        if (result.isErr()) {
            return teamsfx_api_1.err(result.error);
        }
        const appStudioTokenRes = await context.m365TokenProvider.getAccessToken({
            scopes: tools_1.AppStudioScopes,
        });
        if (appStudioTokenRes.isErr()) {
            return teamsfx_api_1.err(appStudioTokenRes.error);
        }
        const appStudioToken = appStudioTokenRes.value;
        const appPackagePath = common_1.getAbsolutePath(args.appPackagePath, context.projectPath);
        if (!(await fs_extra_1.default.pathExists(appPackagePath))) {
            return teamsfx_api_1.err(new common_2.FileNotFoundError(exports.actionName, appPackagePath, "https://aka.ms/teamsfx-actions/teamsapp-update"));
        }
        const archivedFile = await fs_extra_1.default.readFile(appPackagePath);
        // Add capabilities to telemetry properties
        const manifest = ManifestUtils_1.manifestUtils.extractManifestFromArchivedFile(archivedFile);
        if (manifest.isErr()) {
            return teamsfx_api_1.err(manifest.error);
        }
        const manifestTelemetries = teamsfx_api_1.ManifestUtil.parseCommonTelemetryProperties(manifest.value);
        lodash_1.merge(context.telemetryProperties, manifestTelemetries);
        // Fail if Teams app not exists, as this action only update the Teams app, not create
        // See work item 17187087
        const teamsAppId = manifest.value.id;
        if (!isUUID_1.default(teamsAppId)) {
            return teamsfx_api_1.err(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.InvalidTeamsAppIdError.name, errors_1.AppStudioError.InvalidTeamsAppIdError.message(teamsAppId), "https://aka.ms/teamsfx-actions/teamsapp-update"));
        }
        try {
            await appStudioClient_1.AppStudioClient.getApp(teamsAppId, appStudioToken, context.logProvider);
        }
        catch (error) {
            return teamsfx_api_1.err(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.TeamsAppNotExistsError.name, errors_1.AppStudioError.TeamsAppNotExistsError.message(teamsAppId), "https://aka.ms/teamsfx-actions/teamsapp-update"));
        }
        try {
            let message = localizeUtils_1.getLocalizedString("driver.teamsApp.progressBar.updateTeamsAppStepMessage");
            const appDefinition = await appStudioClient_1.AppStudioClient.importApp(archivedFile, appStudioToken, context.logProvider, true);
            message = localizeUtils_1.getLocalizedString("plugins.appstudio.teamsAppUpdatedLog", appDefinition.teamsAppId);
            context.logProvider.verbose(message);
            context.addSummary(message);
            return teamsfx_api_1.ok(new Map([
                [exports.internalOutputNames.teamsAppTenantId, appDefinition.tenantId],
                [exports.internalOutputNames.teamsAppUpdateTime, appDefinition.updatedAt],
            ]));
        }
        catch (e) {
            return teamsfx_api_1.err(results_1.AppStudioResultFactory.SystemError(errors_1.AppStudioError.TeamsAppUpdateFailedError.name, errors_1.AppStudioError.TeamsAppUpdateFailedError.message(teamsAppId, e), "https://aka.ms/teamsfx-actions/teamsapp-update"));
        }
    }
    validateArgs(args) {
        const invalidParams = [];
        if (!args || !args.appPackagePath) {
            invalidParams.push("appPackagePath");
        }
        if (invalidParams.length > 0) {
            return teamsfx_api_1.err(new common_2.InvalidActionInputError(exports.actionName, invalidParams, "https://aka.ms/teamsfx-actions/teamsapp-update"));
        }
        else {
            return teamsfx_api_1.ok(undefined);
        }
    }
};
tslib_1.__decorate([
    lib_1.hooks([addStartAndEndTelemetry_1.addStartAndEndTelemetry(exports.actionName, exports.actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, wrapUtil_1.WrapDriverContext,
        Map]),
    tslib_1.__metadata("design:returntype", Promise)
], ConfigureTeamsAppDriver.prototype, "update", null);
ConfigureTeamsAppDriver = tslib_1.__decorate([
    typedi_1.Service(exports.actionName)
], ConfigureTeamsAppDriver);
exports.ConfigureTeamsAppDriver = ConfigureTeamsAppDriver;
//# sourceMappingURL=configure.js.map